import React, { useState } from 'react';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';

import { createDefaultHeroCarouselItem } from '~/utils/societePayloadBuilder/offersHub/defaults';
import {
  OFFERS_HUB_ENABLED_REGIONS_HELPER_TEXT,
  OFFERS_HUB_LOGO_IMAGE_ID_HELPER_TEXT,
  fieldLabel,
} from '~/utils/societePayloadBuilder/offersHub/fieldLabels';
import {
  formatHeroItemCardTitle,
  getNextHeroCarouselItemDraftLabel,
  moveHeroCarouselItem,
} from '~/utils/societePayloadBuilder/offersHub/offers';
import type { OffersHubHeroCarouselItem } from '~/utils/societePayloadBuilder/offersHub/types';
import { PAYLOAD_BUILDER_PLACEHOLDERS } from '~/utils/societePayloadBuilder/placeholders';

import {
  PAYLOAD_BUILDER_DESCRIPTION_WARNING_LENGTH,
  PAYLOAD_BUILDER_TITLE_WARNING_LENGTH,
  PayloadBuilderImageIdField,
  PayloadBuilderTextField,
  getPayloadBuilderCopyLengthWarningProps,
} from '../FormFields';
import PayloadBuilderRegionChipsField from '../RegionChipsField';
import { SectionCard } from '../SectionCard';

import { OffersHubCtaUrlField } from './OffersHubCtaUrlField';

interface OffersHubHeroItemsSectionProps {
  items: Array<OffersHubHeroCarouselItem>;
  onChange: (items: Array<OffersHubHeroCarouselItem>) => void;
}

function updateItem(
  items: Array<OffersHubHeroCarouselItem>,
  index: number,
  patch: Partial<OffersHubHeroCarouselItem>,
): Array<OffersHubHeroCarouselItem> {
  return items.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item));
}

interface OffersHubHeroItemCardProps {
  item: OffersHubHeroCarouselItem;
  index: number;
  items: Array<OffersHubHeroCarouselItem>;
  onChange: (items: Array<OffersHubHeroCarouselItem>) => void;
}

function OffersHubHeroItemCard({ item, index, items, onChange }: OffersHubHeroItemCardProps) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const borderSoft = alpha(theme.palette.divider, 0.12);
  const title = formatHeroItemCardTitle(item);

  return (
    <Paper elevation={0} sx={{ border: `1px solid ${borderSoft}`, borderRadius: 1, overflow: 'hidden' }}>
      <Box
        sx={{
          px: 1.25,
          py: 0.75,
          bgcolor: alpha(theme.palette.primary.main, expanded ? 0.08 : 0.05),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          borderBottom: expanded ? `1px solid ${borderSoft}` : 'none',
        }}
      >
        <Button
          variant="text"
          color="primary"
          onClick={() => setExpanded((value) => !value)}
          endIcon={
            <ExpandMoreIcon
              fontSize="small"
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: theme.transitions.create('transform', { duration: theme.transitions.duration.shortest }),
              }}
            />
          }
          sx={{
            minWidth: 0,
            justifyContent: 'flex-start',
            px: 0.5,
            textTransform: 'none',
            fontWeight: 700,
            color: 'text.primary',
          }}
        >
          <Typography variant="subtitle2" component="span" noWrap>
            {title}
          </Typography>
        </Button>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
          <Chip label={`Position ${index + 1}`} size="small" color="primary" variant="outlined" />
          <Tooltip title="Move hero item up">
            <Box component="span">
              <IconButton
                aria-label={`Move hero item ${index + 1} up`}
                disabled={index === 0}
                onClick={() => onChange(moveHeroCarouselItem(items, index, -1))}
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
          <Tooltip title="Move hero item down">
            <Box component="span">
              <IconButton
                aria-label={`Move hero item ${index + 1} down`}
                disabled={index === items.length - 1}
                onClick={() => onChange(moveHeroCarouselItem(items, index, 1))}
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
          <Button
            size="small"
            variant={item.isActive ? 'contained' : 'outlined'}
            color={item.isActive ? 'success' : 'inherit'}
            onClick={() => onChange(updateItem(items, index, { isActive: !item.isActive }))}
          >
            {item.isActive ? 'Active' : 'Inactive'}
          </Button>
          <Tooltip title="Remove hero item">
            <IconButton
              aria-label={`Remove hero item ${index + 1}`}
              onClick={() => onChange(items.filter((_, itemIndex) => itemIndex !== index))}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ p: 1.5 }}>
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12 }}>
              <PayloadBuilderRegionChipsField
                label={fieldLabel('enabledRegions')}
                helperText={OFFERS_HUB_ENABLED_REGIONS_HELPER_TEXT}
                regions={item.enabledRegions}
                onChange={(enabledRegions) => onChange(updateItem(items, index, { enabledRegions }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PayloadBuilderTextField
                label={fieldLabel('startAt')}
                type="datetime-local"
                value={item.startAt}
                onChange={(event) => onChange(updateItem(items, index, { startAt: event.target.value }))}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PayloadBuilderTextField
                label={fieldLabel('endAt')}
                type="datetime-local"
                value={item.endAt}
                onChange={(event) => onChange(updateItem(items, index, { endAt: event.target.value }))}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PayloadBuilderTextField
                label={fieldLabel('label')}
                value={item.label}
                onChange={(event) => onChange(updateItem(items, index, { label: event.target.value }))}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <PayloadBuilderImageIdField
                label={fieldLabel('imageId')}
                required
                value={item.imageId}
                onChange={(imageId) => onChange(updateItem(items, index, { imageId }))}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <PayloadBuilderImageIdField
                label={fieldLabel('logoImageId')}
                value={item.logoImageId}
                onChange={(logoImageId) => onChange(updateItem(items, index, { logoImageId }))}
                placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.programLogoImageId}
                infoText={OFFERS_HUB_LOGO_IMAGE_ID_HELPER_TEXT}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PayloadBuilderTextField
                label={fieldLabel('title')}
                value={item.title}
                onChange={(event) => onChange(updateItem(items, index, { title: event.target.value }))}
                required
                {...getPayloadBuilderCopyLengthWarningProps(item.title, PAYLOAD_BUILDER_TITLE_WARNING_LENGTH)}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <PayloadBuilderTextField
                label={fieldLabel('description')}
                value={item.description}
                onChange={(event) => onChange(updateItem(items, index, { description: event.target.value }))}
                required
                multiline
                minRows={2}
                {...getPayloadBuilderCopyLengthWarningProps(
                  item.description,
                  PAYLOAD_BUILDER_DESCRIPTION_WARNING_LENGTH,
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PayloadBuilderTextField
                label={fieldLabel('ctaLabel')}
                value={item.ctaLabel}
                onChange={(event) => onChange(updateItem(items, index, { ctaLabel: event.target.value }))}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <OffersHubCtaUrlField
                value={item.ctaUrl}
                onChange={(event) => onChange(updateItem(items, index, { ctaUrl: event.target.value }))}
                required
              />
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Paper>
  );
}

export default function OffersHubHeroItemsSection({ items, onChange }: OffersHubHeroItemsSectionProps) {
  return (
    <SectionCard
      title="Hero carousel items"
      subtitle="Headline, description, and hero image on the offers hub carousel."
      count={items.length}
    >
      <Stack spacing={2}>
        {items.map((item, index) => (
          <OffersHubHeroItemCard key={item.draftId} item={item} index={index} items={items} onChange={onChange} />
        ))}
        <Button
          variant="outlined"
          onClick={() => onChange([...items, createDefaultHeroCarouselItem(getNextHeroCarouselItemDraftLabel(items))])}
        >
          Add hero item
        </Button>
      </Stack>
    </SectionCard>
  );
}
